import {
  ChangeDetectionStrategy,
  Component,
  ElementRef,
  OnChanges,
  OnDestroy,
  OnInit,
  SimpleChanges,
  effect,
  inject,
  input,
  viewChild,
} from '@angular/core';
import {
  CategoryScale,
  Chart,
  ChartConfiguration,
  ChartData,
  Filler,
  Legend,
  LineController,
  LineElement,
  LinearScale,
  PointElement,
  Tooltip,
} from 'chart.js';
import { ThemeService } from '../../services/theme.service';

Chart.register(CategoryScale, LinearScale, LineController, LineElement, PointElement, Filler, Legend, Tooltip);

export interface SpendTrendPoint {
  date: string;
  cumulativeSpend: number | null;
  plannedCumulativeSpend: number;
}

@Component({
  selector: 'app-spend-trend-chart',
  templateUrl: './spend-trend-chart.component.html',
  styleUrl: './spend-trend-chart.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class SpendTrendChartComponent implements OnInit, OnChanges, OnDestroy {
  readonly points = input<SpendTrendPoint[]>([]);

  readonly chartContextRef = viewChild.required<ElementRef<HTMLCanvasElement>>('ctx');

  private chart: Chart<'line', Array<number | null>, string> | null = null;
  private readonly theme = inject(ThemeService);

  constructor() {
    effect(() => {
      void this.theme.isDark();
      this.updateChart();
    });
  }

  ngOnInit(): void {
    this.updateChart();
  }

  ngOnChanges(_changes: SimpleChanges): void {
    this.updateChart();
  }

  ngOnDestroy(): void {
    this.chart?.destroy();
  }

  private updateChart(): void {
    const points = this.points();
    if (!points.length) {
      this.chart?.destroy();
      this.chart = null;
      return;
    }

    if (!this.chart) {
      this.initChart(points);
      return;
    }

    this.chart.data = this.buildChartData(points);
    this.chart.options = this.buildChartOptions();
    this.chart.update();
  }

  private initChart(points: SpendTrendPoint[]): void {
    const canvas = this.chartContextRef().nativeElement;
    if (!canvas) return;
    if (!canvas.ownerDocument.defaultView?.CanvasRenderingContext2D) return;

    const context = canvas.getContext('2d');
    if (!context) return;

    this.chart = new Chart(context, {
      type: 'line',
      data: this.buildChartData(points),
      options: this.buildChartOptions(),
    } as ChartConfiguration<'line', Array<number | null>, string>);
  }

  private buildChartData(points: SpendTrendPoint[]): ChartData<'line', Array<number | null>, string> {
    const latestActualIndex = this.latestActualPointIndex(points);

    return {
      labels: points.map(point => this.formatDay(point.date)),
      datasets: [
        {
          label: 'This month',
          data: points.map(point => point.cumulativeSpend),
          borderColor: this.token('--app-color-brand-primary', '#00b287'),
          backgroundColor: this.alpha(this.token('--app-color-brand-primary', '#00b287'), 0.1),
          borderWidth: 3,
          tension: 0.32,
          fill: 'origin',
          spanGaps: false,
          pointRadius: points.map((_point, index) => (index === latestActualIndex ? 5 : 0)),
          pointHoverRadius: 5,
          pointBackgroundColor: this.token('--app-surface-elevated', '#ffffff'),
          pointBorderColor: this.token('--app-color-brand-primary', '#00b287'),
          pointBorderWidth: 3,
        },
        {
          label: 'Planned pace',
          data: points.map(point => point.plannedCumulativeSpend),
          borderColor: this.token('--app-border-default', '#d7e8e2'),
          backgroundColor: 'transparent',
          borderWidth: 2,
          tension: 0,
          fill: false,
          pointRadius: 0,
          pointHoverRadius: 0,
        },
      ],
    };
  }

  private buildChartOptions(): NonNullable<ChartConfiguration<'line'>['options']> {
    const gridColor = this.alpha(this.token('--app-border-muted', '#e3ede9'), 0.65);
    const textColor = this.token('--app-text-muted', '#5f7f75');

    return {
      animation: false,
      responsive: true,
      maintainAspectRatio: false,
      layout: {
        padding: {
          top: 8,
          right: 0,
          bottom: 0,
          left: 0,
        },
      },
      elements: {
        line: {
          capBezierPoints: true,
        },
      },
      interaction: {
        intersect: false,
        mode: 'index',
      },
      plugins: {
        legend: {
          display: false,
        },
        tooltip: {
          enabled: false,
        },
      },
      scales: {
        x: {
          grid: {
            display: false,
            drawBorder: false,
          },
          ticks: {
            color: textColor,
            font: {
              size: 10,
              weight: '700',
            },
            maxRotation: 0,
            autoSkip: true,
            maxTicksLimit: 5,
          },
        },
        y: {
          beginAtZero: true,
          grace: '12%',
          grid: {
            color: gridColor,
            borderDash: [2, 7],
            drawBorder: false,
          },
          ticks: {
            display: false,
            maxTicksLimit: 4,
          },
        },
      },
    };
  }

  private formatDay(date: string): string {
    const day = Number(date.slice(-2));
    if (!Number.isFinite(day)) return date;
    return `${day}${this.ordinalSuffix(day)}`;
  }

  private ordinalSuffix(day: number): string {
    const mod100 = day % 100;
    if (mod100 >= 11 && mod100 <= 13) return 'th';

    switch (day % 10) {
      case 1:
        return 'st';
      case 2:
        return 'nd';
      case 3:
        return 'rd';
      default:
        return 'th';
    }
  }

  private latestActualPointIndex(points: SpendTrendPoint[]): number {
    for (let index = points.length - 1; index >= 0; index -= 1)
      if (points[index]?.cumulativeSpend !== null) return index;
    return -1;
  }

  private token(name: string, fallback: string): string {
    return this.theme.resolveCssToken(name) || fallback;
  }

  private alpha(color: string, opacity: number): string {
    const hex = /^#?([a-f\d]{2})([a-f\d]{2})([a-f\d]{2})$/i.exec(color);
    if (!hex) return color;
    const [, red, green, blue] = hex;
    if (!red || !green || !blue) return color;
    return `rgba(${Number.parseInt(red, 16)}, ${Number.parseInt(green, 16)}, ${Number.parseInt(blue, 16)}, ${opacity})`;
  }
}
